import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { generatePath, useMatches, useNavigate, useParams } from 'react-router-dom';
import { Temporal } from '@js-temporal/polyfill';
import Modal from '@ui/modal/modal';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import SelectSingle from '@ui/select/react-select/select-single';
import Switch from '@ui/switch/switch';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Popover from '@ui/popover/popover';
import usePopover from '@ui/popover/use-popover';
import DateRangePicker from '@ui/calendar/date-range-picker';
import toastTrigger from '@ui/toast/toast-trigger';
import type { DateRange } from '@ui/calendar/types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  patchScheduledEmailCustomizations,
  patchScheduledEmailFields,
  updateScheduledEmail,
} from '@/store/slices/scheduled-emails';
import { selectScheduledEmailByUuid, selectScheduledEmails } from '@/store/slices/scheduled-emails/selectors';
import { selectWorkingProject } from '@/store/slices/project/selectors';
import { EmailTypeEnum, type IScheduledEmail, type IScheduledEmailSettings } from '@/types/email.types';
import type { TranslateString } from '@/types/translation.types';
import { updateTranslateKey } from '@/utils/update-translation-key';
import isTranslateString from '@/utils/is-translate-string';
import useLanguageParams from '@/utils/use-language-params';
import globalWindowSize from '@/utils/global-window-size';
import { niceDate, getTimeOfDayOptions } from '@/utils/dates';
import TranslateLangsV2 from '@/utils/translate-langs-v2.json';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { campaignSelectTypes } from '../../utils';
import classes from './schedule-email-modal.module.css';
import { EDIT_PROJECT_ROUTE_IDS } from '@/micros/edit-project/shared/edit-project-route-ids';
import { store } from '@/store/main';
import AudienceListSelect, { type AudienceSelection } from './audience-list-select';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import * as EventEmitter from '@/utils/event-emitter';

const MACHINE_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface Props {
  open: boolean;
  onClose: () => void;
  scheduledEmail: IScheduledEmail | undefined;
}

const LANGUAGE_OPTIONS_V2 = TranslateLangsV2.map((lang) => ({ value: lang.code, label: lang.label }));
const TIME_OPTIONS = getTimeOfDayOptions(15);

function Section({ title, helper, children }: { title: string; helper?: string; children: ReactNode }) {
  return (
    <Container design="GlobalCont2" borderRadius="16px" className={classes.section}>
      <div className={classes.sectionHeader}>
        <Text className="H5" design="GlobalIcoText1">
          {title}
        </Text>
        {helper ? (
          <Text className="L2" color="var(--global-ico-text-4)">
            {helper}
          </Text>
        ) : null}
      </div>
      <div className={classes.sectionBody}>{children}</div>
    </Container>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={classes.field}>
      <Text tag="label" className="L2" color="var(--global-ico-text-4)">
        {label}
      </Text>
      {children}
    </div>
  );
}

function ToggleRow({
  label,
  info,
  on,
  onToggle,
}: {
  label: string;
  info?: boolean;
  on: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <Container className={classes.toggleRow}>
      <div className="flex ai-center gap-4">
        <Text className="L1" color="var(--global-ico-text-2)">
          {label}
        </Text>
        {info ? <Icon name={ICONS.INFO_CIRCLE} size={12} color="var(--global-ico-text-2)" /> : null}
      </div>
      <Switch value={label} on={on} onClick={(_, next) => onToggle(next)} />
    </Container>
  );
}

const getDeliveryDate = (deliveryDate?: string | number | null) => {
  if (typeof deliveryDate === 'string') {
    return new Date(deliveryDate).valueOf();
  }
  return deliveryDate;
};

const MIN_SCHEDULE_LEAD_MS = 5 * 60 * 1000;

// An email can only be scheduled once it has at least one audience - an audience list or a dynamic list -
// and a send time at least 5 minutes out
const isSchedulable = (scheduledEmail: IScheduledEmail | undefined) => {
  if (!scheduledEmail?.audiences?.length && !scheduledEmail?.dynamic_audiences?.length) return false;
  const deliveryTime = getDeliveryDate(scheduledEmail.delivery_date);
  return !!deliveryTime && !Number.isNaN(deliveryTime) && deliveryTime >= Date.now() + MIN_SCHEDULE_LEAD_MS;
};

function ScheduleEmailModal({ open, onClose, scheduledEmail }: Props) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const matches = useMatches();
  const language = useLanguageParams();
  const workingProject = useAppSelector(selectWorkingProject);
  const scheduledEmails = useAppSelector(selectScheduledEmails);
  const { campaignUuid } = useParams<{ campaignUuid: string }>();

  const deliveryDate = scheduledEmail?.delivery_date ?? null;
  const baseLanguage = workingProject?.settings.i18n?.base ?? 'en';
  const popoverPortal = useRef<HTMLDivElement | null>(null);
  const { setClosePopover: closeDatePopover, id: datePopoverId } = usePopover();
  const scrollCleanup = useRef<(() => void) | null>(null);
  const trackScrollEl = useCallback(
    (node: HTMLDivElement | null) => {
      scrollCleanup.current?.();
      scrollCleanup.current = null;
      const scrollContainer = node?.parentElement;
      if (!scrollContainer) return;
      const closeOnScroll = () => closeDatePopover();
      scrollContainer.addEventListener('scroll', closeOnScroll, { passive: true });
      scrollCleanup.current = () => scrollContainer.removeEventListener('scroll', closeOnScroll);
    },
    [closeDatePopover]
  );

  const currentSettings = useMemo<IScheduledEmailSettings>(
    () => ({ showStartTime: true, showAddCalendar: true, usePlainTextEmail: false, ...scheduledEmail?.settings }),
    [scheduledEmail?.settings]
  );

  const patchSettings = useCallback(
    (partial: Partial<IScheduledEmailSettings>) => {
      if (!scheduledEmail?.uuid) return;
      dispatch(
        patchScheduledEmailFields({
          uuid: scheduledEmail.uuid,
          fields: { settings: { ...currentSettings, ...partial } },
        })
      );
    },
    [dispatch, scheduledEmail?.uuid, currentSettings]
  );

  const readTranslate = useCallback(
    (value: TranslateString | undefined) => {
      if (!value) return '';
      if (language) return String(value[language] ?? value.base ?? '');
      return String(value.base ?? '');
    },
    [language]
  );

  const patchIcsField = useCallback(
    (field: 'customTitle' | 'customDescription', input: string) => {
      patchSettings({
        icsSettings: {
          ...currentSettings.icsSettings,
          [field]: updateTranslateKey({
            translateString: currentSettings.icsSettings?.[field] ?? '',
            input,
            baseLanguage,
            language: language ?? null,
          }),
        },
      });
    },
    [patchSettings, currentSettings.icsSettings, baseLanguage, language]
  );

  // Email is always scheduled to be sent in the current user's timezone
  const timeValue = useMemo(
    () =>
      deliveryDate
        ? new Date(deliveryDate).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: MACHINE_TIMEZONE,
          })
        : '',
    [deliveryDate]
  );

  // Ensure the current time is always selectable even if it lands off the 15-minute grid.
  const timeOptions = useMemo(() => {
    // render the current timezone in the select dropdown times to cue the user in that
    // they're selecting the send time in their current timezone
    const timeZoneLabel = deliveryDate
      ? new Intl.DateTimeFormat(navigator.language, {
          timeZone: MACHINE_TIMEZONE,
          timeZoneName: 'short',
        })
          .formatToParts(new Date(deliveryDate))
          .find((part) => part.type === 'timeZoneName')?.value
      : '';

    const labelledOptions = TIME_OPTIONS.map((option) => ({
      value: option.value,
      label: `${option.label} ${timeZoneLabel}`,
    }));

    return !timeValue || TIME_OPTIONS.some((option) => option.value === timeValue)
      ? labelledOptions
      : [{ value: timeValue, label: `${timeValue} ${timeZoneLabel}` }, ...labelledOptions];
  }, [deliveryDate, timeValue]);

  const onChangeTime = useCallback(
    (label: string) => {
      const [hoursMinutes, period] = label.split(' ');
      const [hourStr, minuteStr] = hoursMinutes.split(':');
      let hours = Number(hourStr);
      const minutes = Number(minuteStr);
      if (period === 'PM' && hours !== 12) {
        hours += 12;
      }
      if (period === 'AM' && hours === 12) {
        hours = 0;
      }

      const base = getDeliveryDate(deliveryDate) ?? Date.now();
      const dayInZone = Temporal.Instant.fromEpochMilliseconds(base).toZonedDateTimeISO(MACHINE_TIMEZONE);
      const zoned = dayInZone.with({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
      if (campaignUuid && zoned.epochMilliseconds) {
        dispatch(patchScheduledEmailFields({ uuid: campaignUuid, fields: { delivery_date: zoned.epochMilliseconds } }));
      } else {
        console.error(
          `Cannot set email scheduled time: { uuid: "${campaignUuid}", delivery_date: ${zoned.epochMilliseconds} }`
        );
      }
    },
    [deliveryDate, dispatch, campaignUuid]
  );

  const registrationDisabled = useMemo(
    () =>
      scheduledEmails?.some(
        (email) => email.uuid !== scheduledEmail?.uuid && email.email_type === EmailTypeEnum.event_registration_email
      ),
    [scheduledEmail?.uuid, scheduledEmails]
  );

  const onChangeType = useCallback(
    (value: string) => {
      if (!scheduledEmail) return;
      dispatch(
        patchScheduledEmailFields({ uuid: scheduledEmail.uuid, fields: { email_type: value as EmailTypeEnum } })
      );
    },
    [dispatch, scheduledEmail]
  );

  const onChangeAudiences = useCallback(
    (selection: AudienceSelection) => {
      if (!scheduledEmail?.uuid) return;
      dispatch(patchScheduledEmailFields({ uuid: scheduledEmail.uuid, fields: selection }));
    },
    [dispatch, scheduledEmail?.uuid]
  );

  const subjectValue = useMemo(() => {
    if (!language) return '';
    const subject = scheduledEmail?.working_customizations?.v2?.subject ?? scheduledEmail?.customizations?.v2?.subject;
    if (isTranslateString(subject)) {
      return String(subject[language] ?? subject.base ?? '');
    }
    return String(scheduledEmail?.customizations?.v2?.campaignTitle || '');
  }, [language, scheduledEmail]);

  const onChangeSubject = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!scheduledEmail) return;
      dispatch(
        patchScheduledEmailCustomizations({
          uuid: scheduledEmail.uuid,
          fields: {
            subject: updateTranslateKey({
              translateString: scheduledEmail.customizations?.v2?.subject as TranslateString,
              language: language ?? null,
              baseLanguage,
              input: event.currentTarget.value,
            }),
          },
        })
      );
    },
    [baseLanguage, dispatch, language, scheduledEmail]
  );

  const projectLanguages = useMemo(
    () => Array.from(new Set(workingProject?.sessions.flatMap((session) => session.languages) ?? [])),
    [workingProject]
  );

  const onChangeLanguage = useCallback(
    (nextLanguage: string) => {
      EventEmitter.broadcastSignal(`edit-email-language::tooltip-close`, true);
      const match = matches.find((match) => match.id === EDIT_PROJECT_ROUTE_IDS.CAMPAIGN_EDITOR);
      if (!match) return;
      navigate(
        generatePath('/project/edit/:projectUuid/:language/campaigns/:campaignUuid/edit', {
          projectUuid: String(match.params.projectUuid),
          campaignUuid: String(match.params.campaignUuid),
          language: nextLanguage,
        })
      );
    },
    [matches, navigate]
  );

  const onDateChange = useCallback(
    (dateRange: DateRange) => {
      const timestamp = dateRange.startDate ?? 0;
      if (campaignUuid && timestamp) {
        dispatch(patchScheduledEmailFields({ uuid: campaignUuid, fields: { delivery_date: timestamp } }));
      } else {
        console.error(`Cannot set email scheduled time: { uuid: "${campaignUuid}", delivery_date: ${timestamp} }`);
      }
    },
    [dispatch, campaignUuid]
  );

  const canSave = isSchedulable(scheduledEmail);

  const handleSave = useCallback(async () => {
    // date sync issues from extensive memoization - get the freshest version from redux on save
    const scheduledEmail = selectScheduledEmailByUuid(campaignUuid)(store.getState());
    if (!scheduledEmail || !isSchedulable(scheduledEmail)) return;

    onClose();
    const result = await dispatch(updateScheduledEmail(scheduledEmail));
    if (updateScheduledEmail.rejected.match(result)) {
      toastTrigger('Error saving', {
        description: 'We ran into an issue saving those settings. Please try again.',
        type: 'error',
      });
    }
  }, [dispatch, onClose, campaignUuid]);

  const header = (
    <div className={classes.header}>
      <Text className="H4" design="GlobalIcoText1">
        Schedule email
      </Text>
      <Button clear className={classes.closeButton} onClick={onClose} aria-label="Close">
        <Icon name={ICONS.CLOSE} size={16} color="var(--global-ico-text-2)" />
      </Button>
    </div>
  );

  const footer = (
    <div className={classes.footer}>
      <Button design="LocalBttnSec" onClick={onClose} borderRadius="100px" height="40px" padding="0 20px">
        Cancel
      </Button>
      <Button
        onClick={handleSave}
        disabled={!canSave}
        disabledOpacity={0.5}
        backgroundColor={getThemeColorCSSVariable()}
        hoverBackgroundColor={getThemeColorsCssMixVariable()}
        color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
        borderRadius="100px"
        height="40px"
        padding="0 20px"
      >
        Save
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      animation="drawer"
      withZoom={false}
      showBackdrop
      modalClassName={classes.scheduleDrawer}
      contentClassName={classes.scheduleContent}
      RenderHeaderContent={header}
      RenderFooterContent={footer}
      data-component="ScheduleEmailModal"
      preventClickAway={true}
    >
      <div className={classes.sections} ref={trackScrollEl}>
        <Section title="To" helper="Who are you sending this email to?">
          <Field label="Email Type">
            <SelectSingle
              value={scheduledEmail?.email_type ?? ''}
              options={campaignSelectTypes(registrationDisabled)}
              onChange={onChangeType}
              placeholder="Select type"
              width="100%"
            />
          </Field>
          <Field label="Audience List">
            <AudienceListSelect
              audiences={scheduledEmail?.audiences}
              dynamicAudiences={scheduledEmail?.dynamic_audiences}
              onChange={onChangeAudiences}
              disabled={!scheduledEmail}
            />
          </Field>
        </Section>

        <Section title="Subject" helper="What's the subject line for this email?">
          <InputWithOverlay
            designSize="small"
            isBorderless
            typography="P1"
            wrapperClass={classes.subjectInput}
            inputProps={{
              placeholder: 'Add email subject',
              value: subjectValue,
              onChange: onChangeSubject,
            }}
          />
        </Section>

        <Section title="Send Time" helper="When should we send this email?">
          <div className={classes.twoCol}>
            <Field label="Date">
              <Popover
                id={datePopoverId}
                preferredPosition="bottom-left"
                width="auto"
                portalEl={popoverPortal}
                overflow="visible"
                renderTriggerButton={() => (
                  <Button
                    className="gap-8 flex ai-center"
                    width="100%"
                    height="32px"
                    padding="0 12px"
                    backgroundColor="transparent"
                    border="1px solid var(--gray-500)"
                    hoverBorder="1px solid var(--gray-100)"
                    borderRadius="25px"
                  >
                    <Icon name={ICONS.CALENDAR} size={14} color="var(--global-ico-text-2)" />
                    <Text tag="span" color="var(--global-ico-text-1)" fontSize="13px" fontFamily="inherit">
                      {deliveryDate
                        ? niceDate(deliveryDate, language ?? undefined, 'short', true, false, MACHINE_TIMEZONE)
                        : 'Select date'}
                    </Text>
                  </Button>
                )}
              >
                {({ triggerBounds }) => (
                  <Container
                    style={{
                      position: 'fixed',
                      top: (triggerBounds?.bottom ?? 0) + 8,
                      right: globalWindowSize.innerWidth - (triggerBounds?.right ?? globalWindowSize.innerWidth),
                      zIndex: 1100,
                    }}
                    borderRadius="16px"
                  >
                    <DateRangePicker
                      selectSingleDate={true}
                      hideTimezone
                      borderRadius="16px"
                      value={{ startDate: deliveryDate, endDate: null, selectedTimezone: MACHINE_TIMEZONE }}
                      onChange={onDateChange}
                    />
                  </Container>
                )}
              </Popover>
            </Field>
            <Field label="Time">
              <SelectSingle
                value={timeValue}
                options={timeOptions}
                onChange={onChangeTime}
                placeholder="Select time"
                width="100%"
              />
            </Field>
          </div>
        </Section>

        <Section title="Content Settings">
          <div className={classes.settingsGroup}>
            <ToggleRow
              label="Display date/time in email"
              info
              on={currentSettings.showStartTime ?? true}
              onToggle={(next) => patchSettings({ showStartTime: next })}
            />
            <ToggleRow
              label="Use plain text email"
              on={currentSettings.usePlainTextEmail ?? false}
              onToggle={(next) => patchSettings({ usePlainTextEmail: next })}
            />
          </div>
          <TooltipPortal
            delayStart={150}
            animationType="slide-up"
            id="edit-email-language"
            tooltipContent={
              <Text className="P2 text-center" color="var(--primitives-mode-1-gray-100)">
                Edit content in this language.
                <br />
                Recipients still get the email in their preferred language.
              </Text>
            }
          >
            <Field label="Edit in language">
              <SelectSingle
                value={language ?? 'en'}
                options={LANGUAGE_OPTIONS_V2.filter((option) => projectLanguages.includes(option.value))}
                onChange={onChangeLanguage}
                placeholder="Select edit language"
                width="100%"
              />
            </Field>
          </TooltipPortal>
          <ToggleRow
            label="Add To Calendar"
            info
            on={currentSettings.showAddCalendar ?? true}
            onToggle={(next) => patchSettings({ showAddCalendar: next })}
          />
          <Field label="Calendar Title">
            <InputWithOverlay
              designSize="small"
              inputProps={{
                placeholder: '{{Project name}}',
                value: readTranslate(currentSettings.icsSettings?.customTitle),
                onChange: (event) => patchIcsField('customTitle', event.currentTarget.value),
              }}
            />
          </Field>
          <Field label="Calendar Description">
            <InputWithOverlay
              designSize="small"
              inputProps={{
                placeholder: 'Click here to join',
                value: readTranslate(currentSettings.icsSettings?.customDescription),
                onChange: (event) => patchIcsField('customDescription', event.currentTarget.value),
              }}
            />
          </Field>
        </Section>
      </div>
    </Modal>
  );
}

export default ScheduleEmailModal;
